import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from 'app/core/user/user.service';
import { ActiveConfirmationWizardComponent } from 'app/modules/admin/activ-confirm-wizard/active-confirmation-wizard.component';
import { environment } from 'environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { catchError, finalize, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { User } from '../user/user.types';
import { AuthUtils } from './auth.utils';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = localStorage.getItem('accessToken') ? true : false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);
    private cookieService = inject(CookieService);
    private _dialogService = inject(MatDialog);
    private _isRefreshing: Observable<any> | null = null;

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }
    // -----------------------------------------------------------------------------------------------------
    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    set expireTime(time: string) {
        localStorage.setItem('expireTime', time);
    }

    get expireTime(): string {
        return localStorage.getItem('expireTime') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });

        const body = new URLSearchParams();
        body.set('grant_type', 'password');
        body.set('client_id', environment.clientId);
        // body.set('client_secret', 'WqXxJDyAmJfBg6W3axFxGOaXEAXd7YzS');
        body.set('username', credentials.email);
        body.set('password', credentials.password);
        body.set('scope', 'openid offline_access');

        return this._httpClient
            .post(
                environment.sso + 'realms/' + environment.realm + '/protocol/openid-connect/token',
                body.toString(),
                { headers }
            )
            .pipe(
                switchMap((response: any) => {
                    console.log({ response });
                    this._authenticated = true;

                    // Store the access token in the local storage
                    // Set the authenticated flag to true
                    this.accessToken = response.access_token;
                    this.refreshToken = response.refresh_token;
                    this.expireTime = response.expires_in;
                    localStorage.setItem('lastTokenRefresh', Date.now().toString());

                    console.log('expireTime', this.expireTime);

                    // Try to get user account, but handle 404 gracefully
                    return this._httpClient
                        .get('api/account', {
                            headers: {
                                Authorization: 'Bearer ' + this.accessToken,
                            },
                        })
                        .pipe(
                            switchMap((response2: any) => {
                                this._setUserData(response2);
                                return of(response);
                            }),
                            catchError((accountError) => {
                                // If /api/account returns 404, use the email as username
                                console.warn('Account endpoint not found, using email as username', accountError);
                                const userData = {
                                    id: credentials.email,
                                    login: credentials.email,
                                    firstName: '',
                                    lastName: '',
                                    email: credentials.email,
                                    name: credentials.email,
                                    avatar: 'https://api.dicebear.com/9.x/shapes/svg?seed=' + credentials.email,
                                };
                                this._setUserData(userData);
                                this._authenticated = true;
                                return of(response);
                            })
                        );
                })
            );
    }

    private _setUserData(userData: any): void {
        environment.user = {
            ...userData,
            name: userData.firstName + ' ' + userData.lastName || userData.email || userData.login,
            avatar: userData.avatar || 'https://api.dicebear.com/9.x/shapes/svg?seed=' + (userData.id || userData.email || userData.login),
        };
        // Store the user on the user service
        this._userService.user = {
            ...userData,
            name: userData.firstName + ' ' + userData.lastName || userData.email || userData.login,
            avatar: userData.avatar || 'https://api.dicebear.com/9.x/shapes/svg?seed=' + (userData.id || userData.email || userData.login),
        };
        this._authenticated = true;
    }

    refreshTokenIfActive(): Observable<any> {
        if (this._isRefreshing) {
            return this._isRefreshing;
        }

        // Check if another tab is currently refreshing
        const refreshLock = localStorage.getItem('refresh_lock');
        const lockTime = Number(localStorage.getItem('refresh_lock_time') || '0');
        const now = Date.now();

        // If locked and lock is recent (within 10s), wait and check again
        if (refreshLock === 'true' && (now - lockTime < 10000)) {
             console.log('[Token Refresh] Another tab is refreshing, waiting...');
             return of(null).pipe(
                 switchMap(() => {
                     // Check every 500ms for up to 5s
                     return new Observable(obs => {
                         let attempts = 0;
                         const interval = setInterval(() => {
                             const currentLock = localStorage.getItem('refresh_lock');
                             if (currentLock !== 'true' || attempts > 10) {
                                 clearInterval(interval);
                                 obs.next(true);
                                 obs.complete();
                             }
                             attempts++;
                         }, 500);
                     });
                 })
             );
        }

        // Set lock
        localStorage.setItem('refresh_lock', 'true');
        localStorage.setItem('refresh_lock_time', Date.now().toString());

        this._isRefreshing = this.refreshAccessToken().pipe(
            tap({
                next: () => {
                    console.log('[Token Refresh] Success');
                    localStorage.setItem('lastTokenRefresh', Date.now().toString());
                },
                error: (err) => {
                    console.error('[Token Refresh] Failed', err);
                    // Only sign out on definitive auth errors
                    if (err.status === 400 || err.status === 401) {
                        this.signOut2();
                    }
                },
            }),
            finalize(() => {
                this._isRefreshing = null;
                localStorage.removeItem('refresh_lock');
                localStorage.removeItem('refresh_lock_time');
            }),
            shareReplay(1)
        );
        return this._isRefreshing;
    }

    refreshAccessToken(): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });

        const body = new URLSearchParams();
        body.set('grant_type', 'refresh_token');
        body.set('client_id', environment.clientId);
        body.set('refresh_token', this.refreshToken);

        return this._httpClient
            .post(
                environment.sso + 'realms/' + environment.realm + '/protocol/openid-connect/token',
                body.toString(),
                { headers }
            )
            .pipe(
                tap((response: any) => {
                    this.accessToken = response.access_token;
                    console.log('Access Token', this.accessToken);
                    this.refreshToken = response.refresh_token;
                    console.log('Refresh Token', this.refreshToken);
                    this.expireTime = response.expires_in;
                })
            );
    }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
        });

        const body = new URLSearchParams();
        body.set('grant_type', 'refresh_token');
        body.set('client_id', environment.clientId);
        body.set('refresh_token', this.refreshToken);
        body.set('scope', 'openid offline_access');

        // Sign in using the token
        return this._httpClient
            .post(
                environment.sso + 'realms/' + environment.realm + '/protocol/openid-connect/token',
                body.toString(),
                { headers }
            )
            .pipe(
                switchMap((response: any) => {
                    console.log(response);
                    this._authenticated = true;

                    // Store the access token in the local storage
                    // Set the authenticated flag to true
                    this.accessToken = response.access_token;
                    this.refreshToken = response.refresh_token;
                    this.expireTime = response.expires_in;
                    localStorage.setItem('lastTokenRefresh', Date.now().toString());

                    // Try to get user account, but handle 404 gracefully
                    return this._httpClient
                        .get('api/account', {
                            headers: {
                                Authorization: 'Bearer ' + this.accessToken,
                            },
                        })
                        .pipe(
                            switchMap((response2: any) => {
                                this._setUserData(response2);
                                return of(response);
                            }),
                            catchError((accountError) => {
                                // If /api/account returns 404, use stored email or token info
                                console.warn('Account endpoint not found, using fallback user data', accountError);
                                const savedUser = this._userService.user;
                                const userData = savedUser || {
                                    id: this.accessToken?.substring(0, 10) || 'user',
                                    login: 'user',
                                    firstName: '',
                                    lastName: '',
                                    email: 'user',
                                    name: 'User',
                                    avatar: 'https://api.dicebear.com/9.x/shapes/svg?seed=user',
                                };
                                this._setUserData(userData);
                                return of(response);
                            })
                        );
                })
            );
    }

    signOut2(): void {
        this._authenticated = false;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        this.cookieService.deleteAll();
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        const body = new URLSearchParams();
        body.set('client_id', environment.clientId);
        body.set('refresh_token', this.refreshToken);

        const headers = new HttpHeaders().set(
            'Content-Type',
            'application/x-www-form-urlencoded'
        );

        return this._httpClient
            .post(
                environment.sso + 'realms/' + environment.realm + '/protocol/openid-connect/logout',
                body.toString(),
                { headers }
            )
            .pipe(
                catchError((err) => {
                    console.warn('Server-side logout failed', err);
                    return of(null);
                }),
                finalize(() => {
                    // Clear local storage regardless of success/fail
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('expireTime');
                    localStorage.removeItem('lastTokenRefresh');
                    this.cookieService.deleteAll();
                    this._authenticated = false;

                    // Redirect to login or home page
                    window.location.href = '/sign-in';
                })
            );

        // // Remove the access token from the local storage
        // localStorage.removeItem('accessToken');

        // // Set the authenticated flag to false
        // this._authenticated = false;

        // // Return the observable
        // return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: {
        name: string;
        email: string;
        password: string;
        company: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: {
        email: string;
        password: string;
    }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        if (!this.refreshToken) {
            return of(false);
        }

        return this._httpClient.get<User>('/api/account').pipe(
            tap((authenticated) => (this._authenticated = !!authenticated)),
            switchMap((u) => {
                this._setUserData(u);
                return of(!!u);
            }),
            catchError((e) => {
                console.warn('Check failed', e);
                if (e.status === 401) {
                    // Only sign out if we have no refresh token or refresh failed definitively
                    if (!this.refreshToken) {
                        this.signOut2();
                        return of(false);
                    }
                    // Let the interceptor attempt a refresh on this 401
                    // If it fails, the guard will handle the session end
                    return of(false);
                } else if (e.status === 404) {
                    console.warn('Account endpoint not found, token may still be valid');
                    this._authenticated = true;
                    return of(true);
                } else if (e.status === 0 || e.status >= 500) {
                    // Network error or server error - don't kick user out!
                    // If we have an access token and it's not expired, assume they are still ok
                    if (this.accessToken && !AuthUtils.isTokenExpired(this.accessToken)) {
                        console.log('Server down but token valid, staying logged in');
                        return of(true);
                    }
                }
                return of(false);
            })
        );
    }
}
